<template>
	<Panel
		title="Other"
		isSection
	>
		<div :style="$grid('1')">
			<InputRichDropdown
				v-if="props.deal.hasCoSigner"
				v-model:saturn="submissionDataModel.relationship"
				:list="listOfRelationships"
				:valueMap="(x: ToListItems<CustomersRelationship>) => x.value"
				:display="(x: ToListItems<CustomersRelationship>) => x.display"
				label="Customers Relationship"
				nothingSelected="-- Select Relationship --"
				:invalid="!!validation?.submissionData.relationship?.$silentErrors?.length"
			>
				<template #validation>
					{{ validation?.submissionData?.relationship?.$silentErrors?.[0]?.$message }}
				</template>
			</InputRichDropdown>

			<InputTextbox
				v-if="submissionDataModel.relationship === CustomersRelationship.Other"
				label="Relationship Description"
				v-model:saturn="submissionDataModel.relationshipDescription"
				placeholder="Relationship Description"
				:invalid="!!validation?.submissionData.relationshipDescription?.$silentErrors?.length"
			>
				<template #validation>
					{{ validation?.submissionData?.relationshipDescription?.$silentErrors?.[0]?.$message }}
				</template>
			</InputTextbox>

			<InputRichDropdown
				v-model:saturn="submissionDataModel.bureauType"
				:list="bureausHelper.enumList"
				:valueMap="(x: ToListItems<CreditBureaurs>) => x.value"
				:display="(x: ToListItems<CreditBureaurs>) => x.display"
				label="Suggested Bureau"
				nothingSelected="Experian, Transunion or Equifax ..."
			/>

			<div>
				<InputTextbox
					label="Comments"
					v-model:saturn="submissionDataModel.comment"
					placeholder="Comments to send to the lender/analyst"
					:multiline="true"
					:maxlength="1999"
				/>
				<span style="font-size: 0.9rem; color: grey; justify-self: right">{{ submissionDataModel?.comment?.length ?? 0 }}/2000</span>
			</div>

			<InputCheckbox
				:disabled="disablePassthroughCheckbox"
				v-model:model-value="submissionDataModel.allowLendersPassthrough"
				label="Allow the lender to passthrough"
			/>
		</div>
	</Panel>
</template>
<script setup lang="ts">
	// Classes & Interfaces
	import { CreditBureaurs, CustomersRelationship, VehiclePrimaryUsage } from '@core/classes/SharedEnums';
	import FIDealJacketAdditionalInformation from '@core/classes/FIDealJacketAdditionalInformation';
	import CustomersRelationshipHelper from '@core/_enums/CustomerRelationshipEnumsHelper';

	// Helpers
	import { computed, inject } from 'vue';
	import CreditBureausEnumsHelper from '@core/_enums/BureausEnumsHelper';
	import { ToListItems } from '@core/helpers/enum-helper';

	// Components
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import Panel from '@core/components/Panel.vue';
	import VersionsLender from '@core/classes/LenderVersionClass';
	import FIMenu from '@core/classes/FIMenu';
	import StoreClass from '@core/classes/Store/Store';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';

	export interface IPanelRouteOneSendOtherPanelProps {
		submissionData: FIDealJacketAdditionalInformation;
		lender: VersionsLender;
		deal: FIMenu;
		validation: any;
		forceAllowPassthrough: boolean;
	}
	const $global = inject<SaturnGlobalPlugin>('$global');
	const props = defineProps<IPanelRouteOneSendOtherPanelProps>();
	const emit = defineEmits<{
		(event: 'update:submissionData', value: FIDealJacketAdditionalInformation): void;
	}>();

	const submissionDataModel = computed({
		get: () => props.submissionData,
		set: newValue => {
			emit('update:submissionData', new FIDealJacketAdditionalInformation(newValue));
		},
	});

	const bureausHelper = new CreditBureausEnumsHelper();

	/**
	 * Computes the filtered list of customer relationships that are allowed for this lender
	 *
	 * 1. Determines if any customer is a business entity
	 * 2. Gets the appropriate relationship list based on business status using CustomersRelationshipHelper
	 * 3. Filters out any relationships that are not allowed by the lender
	 *
	 * @returns {ToListItems<CustomersRelationship>[]} Filtered list of allowed customer relationships
	 */
	const listOfRelationships = computed(() => {
		// Check if either the customer or co-customer is a business entity
		const isAnyCustomerBusiness = props.deal.coCustomer.isEntity == true || props.deal.customer.isEntity == true;

		// Get filtered relationships based on business status, then remove any relationships not allowed by the lender
		return CustomersRelationshipHelper.getFilteredList(isAnyCustomerBusiness).filter(x => !notAllowedRelationship.value.includes(x.value));
	});

	const notAllowedRelationship = computed(() => {
		return props.lender?.settings?.notAllowedCustomerRelationships ?? [];
	});
	// Pre populate relationship if is not on the blocked list.
	if (notAllowedRelationship.value.includes(props.deal.customersInfo.relationship)) {
		submissionDataModel.value.relationship = null;
		submissionDataModel.value.relationshipDescription = null;
	} else if (props.deal.customersInfo.relationship != null) {
		submissionDataModel.value.relationship = props.deal.customersInfo.relationship;
		submissionDataModel.value.relationshipDescription = props.deal.customersInfo.relationshipDescription;
	}

	const disablePassthroughCheckbox = computed(() => {
		if (props.deal.vehicle.registrationInfo.primaryUsage === VehiclePrimaryUsage.BUSINESS || props.deal.customer.isEntity || props.deal.coCustomer?.isEntity) {
			return false;
		}
		var store = $global.selectedStore as StoreClass;
		var isAllySoftPullEnabled = store.storeSettings.softPullAllyProgram;

		return isAllySoftPullEnabled && props.lender.lenderCode == 'ALLY';
	});
</script>
<style scoped></style>
